import {
  Avatar,
  Button,
  Circle,
  Divider,
  Flex,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import moment from 'moment'
import { CheckCircle } from 'phosphor-react'
import { useTranslation } from 'react-i18next'
import { TbReload } from 'react-icons/tb'

import ArrowRepeatRotateIcon from '@/ui/assets/ArrowRepeatRotateIcon'
import CalendarIcon from '@/ui/assets/CalendarIcon'
import FourSquareIcon from '@/ui/assets/FourSquareIcon'
import ShearchLoupeIcon from '@/ui/assets/ShearchLoupeIcon'
import XIcon from '@/ui/assets/XIcon'
import TableError from '@/ui/components/Status/Error/TableError'
import TableBodyLoading from '@/ui/components/Status/Loading/TableBodyLoading'
import TableRowNotFound from '@/ui/components/Status/NotFound/TableRowNotFound'
import { usePatientContext } from '@/ui/contexts/PatientContext'

import CAADailyRecordsFilterMenu from './components/CAADailyRecordsFilterMenu'
import { useCAADailyRecordsContext } from './context/CAADailyRecordsContext'
import { useListCAADailyRecords } from './hooks/useListCAADailyRecords'

export default function CAADailyRecords({
  isManager = false,
  isPatientView = false
}: {
  isManager?: boolean
  isPatientView?: boolean
}) {
  const { t } = useTranslation()
  const { patient } = usePatientContext()
  const { filter } = useCAADailyRecordsContext()
  const listCAADailyRecords = useListCAADailyRecords({
    isManager: isManager,
    date: filter.date,
    patientId: patient?.id || undefined,
    personaName: filter.personaName,
    availability: filter.availability,
    typeCAA: filter.typeCAA
  })

  return (
    <Flex direction="column" overflowY={'auto'} flex={'1'}>
      <Flex
        wrap={'wrap'}
        justify={'space-between'}
        align={'center'}
        w={'full'}
        borderWidth={'1px'}
        p={2}
        borderRadius={'4px'}
        mb={4}
      >
        <Flex
          flex={1}
          color={'custom.gray.200'}
          fontSize={'16'}
          fontWeight={500}
          gap={2}
        >
          {filter.personaName && (
            <Flex align={'baseline'} gap={1}>
              <ShearchLoupeIcon boxSize={'18px'} />
              <Text> {filter.personaName}, </Text>
            </Flex>
          )}
          {filter.date && (
            <Flex align={'center'} gap={1}>
              <CalendarIcon boxSize={'18px'} />
              <Text>{moment(filter.date).format('L')},</Text>
            </Flex>
          )}

          <Flex align={'center'} gap={1}>
            <FourSquareIcon boxSize={'24px'} />
            {!filter.typeCAA ? (
              <Text>{t(`caa.table.caaTypes.allUses`)},</Text>
            ) : (
              <Text>{t(`caa.table.caaTypes.${filter.typeCAA}`)},</Text>
            )}
          </Flex>
          <Flex align={'center'} gap={1}>
            <ArrowRepeatRotateIcon boxSize={'24px'} />
            {!filter.availability ? (
              <Text>{t(`caa.table.availabilityTypes.allAvailability`)}</Text>
            ) : (
              <Text>
                {t(`caa.table.availabilityTypes.${filter.availability}`)}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex gap={4}>
          <Button
            backgroundColor={'custom.blue.darker'}
            color="white"
            fontSize={'20px'}
            isLoading={listCAADailyRecords.isFetching}
            isDisabled={listCAADailyRecords.isFetching}
            onClick={() => listCAADailyRecords.refetch()}
          >
            <TbReload />
          </Button>
          <CAADailyRecordsFilterMenu />
        </Flex>
      </Flex>
      <Flex mb={4} gap={12} justify={'space-between'} align={'center'}>
        <Flex
          gap={4}
          p={4}
          align={'center'}
          borderWidth={'1px'}
          borderRadius={'4px'}
          w={'full'}
        >
          <Circle size={'48px'} bg={'custom.gray.900'}>
            <FourSquareIcon fontSize={'32px'} />
          </Circle>
          <Flex direction={'column'}>
            <Text
              fontSize={'18px'}
              fontWeight={'500'}
              color={'custom.gray.100'}
            >
              {t('caa.usesTotal')}
            </Text>
            <Flex gap={2} h={'40px'} w={'full'}>
              <Flex alignItems={'baseline'} gap={1}>
                {listCAADailyRecords.isLoading ? (
                  <Skeleton h={'40px'} w={'80px'} />
                ) : (
                  <>
                    <Text fontWeight={'600'} fontSize={'28px'}>
                      {listCAADailyRecords.data?.modeling ?? 0}
                    </Text>
                    <Text fontSize={'12px'} textColor={'custom.gray.400'}>
                      {t('caa.modeling')}
                    </Text>
                  </>
                )}
              </Flex>
              <Divider orientation="vertical" />
              <Flex alignItems={'baseline'} gap={1}>
                {listCAADailyRecords.isLoading ? (
                  <Skeleton h={'40px'} w={'130px'} />
                ) : (
                  <>
                    <Text fontWeight={'600'} fontSize={'28px'}>
                      {listCAADailyRecords.data?.clientUsage ?? 0}
                    </Text>
                    <Text fontSize={'12px'} textColor={'custom.gray.400'}>
                      {t('caa.customerUses')}
                    </Text>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {!isManager && !isPatientView && (
          <>
            <Flex
              gap={4}
              p={4}
              align={'center'}
              borderWidth={'1px'}
              borderColor={'custom.green.800'}
              borderRadius={'4px'}
              w={'full'}
            >
              <Circle size={'48px'} bg={'custom.green.light'}>
                <Icon
                  as={CheckCircle}
                  fontSize={'32px'}
                  color={'custom.green.dark'}
                />
              </Circle>
              <Flex direction={'column'}>
                <Text
                  fontSize={'18px'}
                  fontWeight={'500'}
                  color={'custom.gray.100'}
                >
                  {t('caa.table.availabilityTypes.AVAILABLE')}
                </Text>
                <Flex alignItems={'baseline'} gap={1}>
                  {listCAADailyRecords.isLoading ? (
                    <Skeleton h={'20px'} w={'full'} />
                  ) : (
                    <>
                      <Text fontWeight={'600'} fontSize={'28px'}>
                        {listCAADailyRecords.data?.availabilities?.find(
                          (item) => item.availability === 'AVAILABLE'
                        )?.amount ?? 0}
                      </Text>
                      <Text fontSize={'12px'} textColor={'custom.gray.400'}>
                        {t('general.common.terms.session', {
                          count:
                            listCAADailyRecords.data?.availabilities?.find(
                              (item) => item.availability === 'AVAILABLE'
                            )?.amount ?? 0
                        })}
                      </Text>
                    </>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Flex
              gap={4}
              p={4}
              align={'center'}
              borderWidth={'1px'}
              borderColor={'custom.green.800'}
              borderRadius={'4px'}
              w={'full'}
            >
              <Circle size={'48px'} bg={'custom.red.light'}>
                <XIcon fontSize={'32px'} color={'custom.red.default'} />
              </Circle>
              <Flex direction={'column'}>
                <Text
                  fontSize={'18px'}
                  fontWeight={'500'}
                  color={'custom.gray.100'}
                >
                  {t('caa.table.availabilityTypes.UNAVAILABLE')}
                </Text>
                <Flex alignItems={'baseline'} gap={1}>
                  {listCAADailyRecords.isLoading ? (
                    <Skeleton h={'20px'} w={'full'} />
                  ) : (
                    <>
                      <Text fontWeight={'600'} fontSize={'28px'}>
                        {listCAADailyRecords.data?.availabilities?.find(
                          (item) => item.availability === 'UNAVAILABLE'
                        )?.amount ?? 0}
                      </Text>
                      <Text fontSize={'12px'} textColor={'custom.gray.400'}>
                        {t('general.common.terms.session', {
                          count:
                            listCAADailyRecords.data?.availabilities?.find(
                              (item) => item.availability === 'UNAVAILABLE'
                            )?.amount ?? 0
                        })}
                      </Text>
                    </>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </>
        )}
      </Flex>

      <Flex direction="column" h={'full'} justify="space-between">
        {listCAADailyRecords.isLoading ? (
          <Table>
            <Tbody>
              <TableBodyLoading rows={4} />
            </Tbody>
          </Table>
        ) : listCAADailyRecords.isError ? (
          <Table>
            <Tbody>
              <TableError message={listCAADailyRecords.error?.zodMessage} />
            </Tbody>
          </Table>
        ) : (
          <Table
            variant={'simple'}
            size={'sm'}
            borderRadius={'md'}
            border={'solid 1px #DEE2E6'}
          >
            <Thead>
              <Tr
                position={'sticky'}
                top={0}
                h={'2.5rem'}
                backgroundColor={'#F5F5FF'}
                zIndex={1}
                css={`
                  & > * {
                    border-bottom: solid 1px #dee2e6 !important;
                    padding-top: 1rem !important;
                    padding-bottom: 1rem !important;
                    color: #495057 !important;
                    font-weight: 400 !important;
                    text-transform: capitalize !important;
                    font-size: 14px !important;
                  }
                `}
              >
                {!isPatientView && <Th>{t('caa.table.clients')}</Th>}
                <Th>{t('caa.table.therapists')}</Th>
                <Th>{t('caa.table.sessionTime')}</Th>
                <Th>{t('caa.table.availability')}</Th>
                <Th>{t('caa.table.modeling')}</Th>
                <Th>{t('caa.table.customerUses')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {!listCAADailyRecords.data?.caa ||
              listCAADailyRecords.data?.caa.length === 0 ? (
                <TableRowNotFound />
              ) : (
                listCAADailyRecords.data?.caa.map((item, index) => {
                  const {
                    patient,
                    therapist,
                    start,
                    end,
                    availability,
                    modeling,
                    customerUse
                  } = item

                  return (
                    <Tr key={index}>
                      {!isPatientView && (
                        <Td>
                          <Flex
                            key={patient.userId}
                            align={'center'}
                            gap={2}
                            w={'full'}
                          >
                            <Avatar
                              name={patient.username}
                              src={patient.avatarUrl}
                              boxSize="24px"
                            />
                            <Tooltip label={patient.username} hasArrow>
                              <Text
                                fontSize={'14px'}
                                color={'custom.gray.100'}
                                isTruncated
                              >
                                {patient.username || '-'}
                              </Text>
                            </Tooltip>
                          </Flex>
                        </Td>
                      )}
                      <Td maxW="250px">
                        <Flex align={'center'} gap={2} w={'full'}>
                          <Avatar
                            name={therapist.username}
                            src={therapist.avatarUrl}
                            boxSize="24px"
                          />
                          <Tooltip label={therapist.username} hasArrow>
                            <Text
                              fontSize={'14px'}
                              color={'custom.gray.100'}
                              isTruncated
                            >
                              {therapist.username || '-'}
                            </Text>
                          </Tooltip>
                        </Flex>
                      </Td>

                      <Td>
                        {`${moment(start).format('HH:mm')} - ${moment(
                          end
                        ).format('HH:mm')}`}
                      </Td>
                      <Td>
                        <Flex
                          borderRadius={'full'}
                          w={'fit-content'}
                          px={2}
                          py={1}
                          bg={
                            availability === 'AVAILABLE'
                              ? 'custom.green.light'
                              : 'custom.red.light'
                          }
                          color={
                            availability === 'AVAILABLE'
                              ? 'custom.green.dark'
                              : 'custom.red.default'
                          }
                          fontWeight={500}
                        >
                          {t(`caa.table.availabilityTypes.${availability}`)}
                        </Flex>
                      </Td>
                      <Td>
                        {modeling === 0
                          ? '-'
                          : `${modeling} ${t('general.common.terms.times', {
                              count: modeling
                            })}`}
                      </Td>
                      <Td>
                        {customerUse === 0
                          ? '-'
                          : `${customerUse} ${t('general.common.terms.times', {
                              count: customerUse
                            })}`}
                      </Td>
                    </Tr>
                  )
                })
              )}
            </Tbody>
          </Table>
        )}
      </Flex>
    </Flex>
  )
}
